var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { ANCHOR_TAG, IS_DEV, LINK_CLICK_EVENT_TYPE, NON_LINK_CLICK_EVENT_TYPE } from '../constants/plugin-constants';
import { addToEventListenersRecord } from './event-listener-utils';
var getCommonAttributeValues = function (element) {
    var contentHeight = getContentHeight(element);
    var record = {
        title: element.getAttribute('title'),
        ariaLabel: element.getAttribute('aria-label'),
        role: element.getAttribute('role'),
        ariaHidden: element.getAttribute('aria-hidden'),
        contentHeight: contentHeight !== null ? contentHeight.toString() : null,
        mouseLocation: getXYCoordinatesOfElement(element),
        textContent: element.textContent,
        tagName: element.tagName,
        viewPortDimensions: "".concat(window.innerWidth, "/").concat(window.innerHeight),
        documentDimensions: "".concat(document.documentElement.scrollWidth, "/").concat(document.documentElement.scrollHeight),
    };
    return record;
};
export var getImageAttributeValues = function (imgElement) {
    var record = {
        altText: imgElement.getAttribute('alt'),
        imageSource: imgElement.src,
    };
    return __assign(__assign({}, getCommonAttributeValues(imgElement)), record);
};
export var getMediaAttributeValues = function (mediaElement) {
    var record = {
        mediaSource: mediaElement.src,
    };
    return __assign(__assign({}, getCommonAttributeValues(mediaElement)), record);
};
export var getInputAttributeValues = function (inputElement) {
    var record = {
        inputType: inputElement.getAttribute('type'),
        inputValue: inputElement.getAttribute('value'),
        inputName: inputElement.getAttribute('name'),
    };
    return __assign(__assign({}, getCommonAttributeValues(inputElement)), record);
};
export var getAnchorAttributeValues = function (anchorElement) {
    return __assign({}, getCommonAttributeValues(anchorElement));
};
export var getPossibleElementAttributeValues = function (element) {
    // Image element
    if (element instanceof HTMLImageElement) {
        return getImageAttributeValues(element);
    }
    // Media elements (Audio, Video)
    else if (element instanceof HTMLMediaElement) {
        return getMediaAttributeValues(element);
    }
    // Input elements
    else if (element instanceof HTMLInputElement || element instanceof HTMLButtonElement) {
        return getInputAttributeValues(element);
    }
    // Anchor element
    else if (element instanceof HTMLAnchorElement) {
        return getAnchorAttributeValues(element);
    }
    // All other elements
    return getCommonAttributeValues(element);
};
/**
 *
 * Returns the value of the specified attribute from the passed element (or)
 * any from any closest parent element.
 *
 * eg.) <div data-awsma-event-content-id="123">
 *        <div data-awsma-event-name="click">
 *          <div id="dummyDiv">Dummy Div</div>
 *          <div data-awsma-event-content-id="234" id="secondDummyDiv">Second Dummy Div</div>
 *        </div>
 *      </div>
 *
 * calling the getClosest:
 *
 * 1. with the attribute in the same element
 *    getClosest(document.getElementById('secondDummyDiv'), '[data-awsma-event-content-id]', 'data-awsma-event-content-id)
 *      returns 234.
 * 2. with the attribute not present in the same element
 *    getClosest(document.getElementById('dummyDiv'), '[data-awsma-event-content-id]', 'data-awsma-event-content-id)
 *      returns 123.
 *
 * @param {HTMLElement} element
 * @param {string} selector
 * @param {string} attrName
 * @returns {string}
 */
export var getClosestValue = function (element, selector, attrName) {
    var closestParent = element.closest(selector);
    return closestParent ? closestParent.getAttribute(attrName) : null;
};
/**
 *
 * A utility method which helps to use template strings in constants
 * eg.) templateText = 'This is a sample {0} to demonstrate {1}';
 *
 * how to use?
 *
 * const finalText = getTemplateString(templateText)('text', 'templates');
 *
 * or
 *
 * const templateFunctionWithText = getTemplateString(templateText);
 * const anotherFinalTextSomePlaceElse = templateFunctionWithText('text', 'templates');
 *
 * NOTE: The interpolation is 0 indexed.
 *
 * @param {string} text
 * @returns {MethodReturnsString}
 */
export var getTemplateString = function (text) {
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return args.reduce(function (previousValue, currentValue, currentIndex) {
            return previousValue.replace("{".concat(currentIndex, "}"), currentValue);
        }, text);
    };
};
export var isSelectorElementPresent = function (target, selector) {
    return target instanceof HTMLElement && Boolean(target.closest(selector));
};
export var isEventTrackingIgnored = function (event, ignoreSelector) {
    var htmlElement = event.target;
    return ignoreSelector !== undefined && htmlElement.hasAttribute(ignoreSelector);
};
export var onAddEventListener = function (parentElement, eventType, _a) {
    var callback = _a.callback, ignoreSelector = _a.ignoreSelector, pluginName = _a.pluginName;
    // Called once per session. Good to instantiate a function inside.
    var eventListenerCallback = function (event) {
        // Invoke the source callback only if the tracking is not ignored.
        if (!isEventTrackingIgnored(event, ignoreSelector)) {
            callback(event);
        }
    };
    parentElement.addEventListener(eventType, eventListenerCallback);
    // Maintain the event listener in order to clear during page unload or
    // whenever plugin destroy is invoked specific to a plugin.
    addToEventListenersRecord(pluginName, {
        eventType: eventType,
        element: parentElement,
        callback: eventListenerCallback,
    });
};
/**
 * HTML anchor elements (<a>) are used to represent links in a document.
 * Sometimes, they can be used to handle modal open, accoridion toggle, and such js interactions without
 * providing a link. (eg. href="#" or href="javascript:void(0);"). They don't navigate the user to a different page
 * but would help with handling of js interactions. We can call them as "FALSE Links"
 *
 * We don't want to track those category of links.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
export var isValidLink = function (element) {
    if (element.tagName.toLowerCase() === ANCHOR_TAG) {
        var link = element.getAttribute('href');
        if (link) {
            link = link.trim();
            return link !== '' && link !== '#' && !link.startsWith('javascript:');
        }
    }
    return false;
};
/**
 * Traverses up the tree using the .closest()
 * to get the contentIds present in the ancestoral hierarchy of the initial childElement passed.*
 *
 * @param {HTMLElement} childElement
 * @param {HTMLElement} parentElement
 * @param {Record<string, string>} attrMap
 * @param {Array<string>} [resultString=[]]
 * @return {string} concatenatedContentId
 */
var traverseNodes = function (childElement, parentElement, attrMap, resultString) {
    var _a;
    if (resultString === void 0) { resultString = []; }
    if (!parentElement) {
        return resultString.length === 0 ? '' : resultString.reverse().join(attrMap['delimiter']);
    }
    var contentId = parentElement.getAttribute(attrMap['contentIdAttribute']); // Returns the contentId present in the data-attr of that element.
    resultString.push(contentId);
    childElement = parentElement;
    // Need to do .parentNode to avoid closest() matching for the same element.
    parentElement = (_a = parentElement.parentNode) === null || _a === void 0 ? void 0 : _a.closest(attrMap['contentIdAttributeWithInBrackets']);
    return traverseNodes(childElement, parentElement, attrMap, resultString);
};
/**
 * Returns the concatenated content ids
 *
 * @param {HTMLElement} element
 * @param {string} contentIdAttribute (eg. data-awsma-event-content-id)
 * @param {string} delimiter (eg. '|', ',', ...)
 * @returns {string} (eg. EC2 page|EC2 page bottom banner or '')
 */
export var getAncestorContentIds = function (element, contentIdAttribute, delimiter) {
    if (delimiter === void 0) { delimiter = '|'; }
    var contentIdAttributeWithInBrackets = getAsAttributeSelector(contentIdAttribute);
    return traverseNodes(element, element.closest(contentIdAttributeWithInBrackets), {
        delimiter: delimiter,
        contentIdAttribute: contentIdAttribute,
        contentIdAttributeWithInBrackets: contentIdAttributeWithInBrackets,
    });
};
/**
 * Returns the [x,y] co-ordinates for the element if present.
 * @param {HTMLElement} element
 * @returns {[number, number] | null}
 */
export var getXYCoordinatesOfElement = function (element) {
    if (element instanceof HTMLElement) {
        var rect = element.getBoundingClientRect();
        return "".concat(rect.x, ",").concat(rect.y);
    }
    return null;
};
/**
 * Returns the height of the element if present.
 * @param {HTMLElement} element
 * @returns {number | null}
 */
export var getContentHeight = function (element) {
    if (element instanceof HTMLElement) {
        var rect = element.getBoundingClientRect();
        return rect.height;
    }
    return null;
};
/**
 *
 * @param {HTMLAnchorElement} anchorElement
 * @param {string} contentIdDataAttr
 * @returns {DataLayerLibrary.DataLayerClickEvent}
 */
export var getEventFieldsForAnchorClick = function (htmlElement, anchorElement, contentIdDataAttr) {
    var _a;
    var mouseLocation = getPossibleElementAttributeValues(anchorElement).mouseLocation;
    var htmlElementAttributeValues = getPossibleElementAttributeValues(htmlElement);
    var eventType = LINK_CLICK_EVENT_TYPE;
    var eventContentId = getAncestorContentIds(htmlElement, contentIdDataAttr);
    var contentHeight = getContentHeight(anchorElement);
    var eventContentURL = addCOMTopLevelDomain(anchorElement.href, IS_DEV);
    var eventContentTitle = anchorElement.getAttribute('title');
    var eventValue = ((_a = anchorElement.childNodes[0]) === null || _a === void 0 ? void 0 : _a.textContent) || ''; // This ensure we get the text only from the current element and not including the children's.
    // If eventContentTitle is present, pick that otherwise, pick eventContentURL.
    var eventName = "[".concat(eventType, ": ").concat(eventContentTitle || eventContentURL, "]");
    return {
        eventType: eventType,
        eventName: eventName,
        eventContentURL: eventContentURL,
        eventContentTitle: eventContentTitle,
        eventContentId: eventContentId,
        eventTimestamp: getEpochTimestamp(),
        eventValue: eventValue,
        mouseLocation: mouseLocation,
        contentHeight: contentHeight,
        customData: htmlElementAttributeValues,
    };
};
/**
 *
 * @param {HTMLElement} htmlElement
 * @param {string} contentIdDataAttr
 * @param {string} eventNameDataAttr
 * @param {string} eventContentTitleAttr
 * @returns {DataLayerLibrary.DataLayerClickEvent}
 */
export var getEventFieldsForNonLinkClicks = function (htmlElement, _a) {
    var eventContentIdAttr = _a.eventContentIdAttr, eventContentTitleAttr = _a.eventContentTitleAttr, eventNameAttr = _a.eventNameAttr, eventValueAttr = _a.eventValueAttr;
    var concatenatedContentId = getAncestorContentIds(htmlElement, eventContentIdAttr);
    var eventName = getClosestValue(htmlElement, getAsAttributeSelector(eventNameAttr), eventNameAttr);
    var eventValue = getClosestValue(htmlElement, getAsAttributeSelector(eventValueAttr), eventValueAttr);
    var eventContentTitle = getClosestValue(htmlElement, getAsAttributeSelector(eventContentTitleAttr), eventContentTitleAttr);
    var mouseLocation = getXYCoordinatesOfElement(htmlElement);
    var contentHeight = getContentHeight(htmlElement);
    var htmlElementAttributeValues = getPossibleElementAttributeValues(htmlElement);
    return {
        eventType: NON_LINK_CLICK_EVENT_TYPE,
        eventName: eventName,
        eventContentTitle: eventContentTitle,
        eventContentId: concatenatedContentId,
        eventValue: eventValue,
        eventTimestamp: getEpochTimestamp(),
        mouseLocation: mouseLocation,
        contentHeight: contentHeight,
        customData: htmlElementAttributeValues,
    };
};
/**
 * Returns the current time in EpochTimeStamp
 *
 * @returns {EpochTimeStamp}
 */
export var getEpochTimestamp = function () {
    return Date.now();
};
/**
 * If data-attribute is present, returns as [data-attribute]. This is required to perform .matches
 * Same with other attributes such as name, type, ...
 *
 * @param {string} attr
 * @returns {string}
 */
export var getAsAttributeSelector = function (attr) {
    return "[".concat(attr, "]");
};
/**
 * If IS_DEV is set and the eventContentURL is a local host domain, add .com to the URL. This is required for validation using
 * regex for URL in data layer
 * https://code.amazon.com/packages/AWSMarketingClientSideDataServiceModel/blobs/44a8a796ccb04af9796d4f2cf4336ff68923e839/--/model/d2c-collection-operation-v1.xml#L243
 *
 * @param {string} url (eg https://localhost:5173/NewPage)
 * @returns {string} url with .com appended https://localhost.com:5173/NewPage
 */
export var addCOMTopLevelDomain = function (url, isDev) {
    if (isDev) {
        if (url.includes('localhost') && !url.includes('localhost.com')) {
            url = url.replace('localhost', 'localhost.com');
        }
    }
    return url;
};
//# sourceMappingURL=data:application/json;base64,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